import React, { useEffect, useMemo, useState } from 'react'
import st from './NewAndWonDeals.module.scss'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import { Link, useLocation, useParams } from 'react-router-dom'
import { superAxiosRequest } from 'axiosApi'
import useLocalization from 'hooks/useLocalization'
import PageHeader from 'components/UI/PageHeader/PageHeader'
import Pagination from 'components/UI/Pagination/Pagination'
import { observer } from 'mobx-react'
import listingStore from 'Stores/listingStore'
import Loader from '../../UI/Loader/Loader'

const NewDeals = (props) => {
  const [deals, setDeals] = useState(null)
  const [meta, setMeta] = useState({})
  const [loading, setLoading] = useState(false)
  const [reasons, setReasons] = useState([])

  const [filter, updateFilter] = useLocalStorageFilter({})
  const { page, perPage, setPage, setPerPage } = listingStore

  const { isOpenFilter, closeFilter, setTextValues, filterTextValues } =
    AsideFilterStore

  const location = useLocation()
  const { id } = useParams()

  const queryParams = new URLSearchParams(location.search)
  const dealsIds = queryParams.get('deals_ids')
  const statusNameFromUrl = queryParams.get('status_name')

  const dealsPayload = dealsIds
    ? { deals_ids: dealsIds.split(',').map(Number) }
    : null

  const statusId = useMemo(() => props.match.params.status_id, [])

  const { translate } = useLocalization({
    section: 'groups',
    subsection: 'all_groups',
    dict: {
      filter: 'Filter',
      search: 'Search',
      reset_all: 'Reset All',
      apply: 'Apply',
      new_deals: 'New deals',
      search_placeholder: 'Search',
      deals_name: 'Deals name',
      telephone: 'Telephone',
      responsible: 'Responsible',
      creation_date: 'Creation date',
      deal_stage: 'Deal stage',
      whatsapp: 'WhatsApp',
      reject_reason: 'Причина отказа',
    },
  })

  // 1. Загрузка справочника причин
  useEffect(() => {
    const fetchReasons = async () => {
      try {
        const response = await superAxiosRequest({
          link: 'settings/project_settings/deal_reject_reason_choices',
          method: 'get',
          crm: true,
        })
        const data = response?.data || response
        if (data?.value?.choices) {
          setReasons(data.value.choices)
        }
      } catch (error) {
        console.error('Ошибка загрузки причин:', error)
      }
    }
    fetchReasons()
  }, [])

  const filterCfg = useMemo(
    () => [
      {
        type: 'select',
        label: translate?.reject_reason || 'Причина отказа',
        placeholder: translate?.reject_reason || 'Причина отказа',
        values:
          reasons?.map((el) => ({
            id: el.name,
            name: el.name,
          })) || [],
        sort_key: 'name',
        nameState: 'reject_reason',
      },
    ],
    [reasons, translate],
  )

  // 2. Формирование запроса
  const fetchDealsByIds = async () => {
    if (!dealsPayload?.deals_ids?.length) {
      setDeals([])
      setMeta({})
      setLoading(false)
      return
    }

    try {
      setLoading(true)

      const requestParams = {
        limit: perPage,
        offset: (page - 1) * perPage,
      }

      const payload = {
        ...dealsPayload,
        ...(filter?.reject_reason && {
          reject_reason: Array.isArray(filter.reject_reason)
            ? filter.reject_reason.join(',')
            : filter.reject_reason,
        }),
      }

      const response = await superAxiosRequest(
        {
          method: 'post',
          link: 'integration/deals_list',
          crm: true,
          params: requestParams,
        },
        payload,
      )

      setDeals(response?.data?.result || response?.data || [])
      setMeta(response?.data?._meta || {})
      listingStore.meta = {
        ...listingStore.meta,
        count_items: response?.data?._meta?.count_items || 0,
      }
    } catch (error) {
      console.error('Ошибка при POST-запросе:', error)
      setDeals([])
      setMeta({})
    } finally {
      setLoading(false)
    }
  }

  const getContactInfo = (deal) => {
    const client = deal?.client || deal?.lead?.client
    return {
      phone: client?.phone || '',
      whatsapp: client?.whatsapp || '',
    }
  }

  useEffect(() => {
    fetchDealsByIds()
  }, [filter, page, perPage, statusId, id])

  useEffect(() => AsideFilterStore.unmountFilter, [])

  const funnelName = useMemo(() => {
    if (statusNameFromUrl) return statusNameFromUrl
    if (deals?.[0]?.funnels?.[0]?.name) return deals[0].funnels[0].name
    return 'Deals'
  }, [deals, statusNameFromUrl])

  const handleFilterChange = (newFilter) => {
    updateFilter(newFilter)

    const reasonValue = newFilter?.reject_reason
    if (
      reasonValue &&
      (Array.isArray(reasonValue) ? reasonValue.length > 0 : true)
    ) {
      const displayValue = Array.isArray(reasonValue)
        ? reasonValue.join(', ')
        : reasonValue

      setTextValues([
        ...filterTextValues.filter((f) => f.key !== 'reject_reason'),
        {
          key: 'reject_reason',
          label: translate.reject_reason,
          value: displayValue,
        },
      ])
    } else {
      setTextValues(filterTextValues.filter((f) => f.key !== 'reject_reason'))
    }
  }

  const handleClearReason = () => {
    const { reject_reason, ...restFilter } = filter
    updateFilter(restFilter)
    setTextValues(filterTextValues.filter((f) => f.key !== 'reject_reason'))
  }

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  return (
    <div>
      <div className={st.header}>
        <PageHeader title={funnelName} />

        <div className={st.searchFilterValue}>
          <SearchBooks
            onSearch={() => {}}
            onClear={handleClearReason}
            translate={translate}
            placeholder={translate.reject_reason}
            value=''
            isFilterEmpty={!reasons.length}
          />

          {!!filterTextValues.length && (
            <div className={st.searchFilterValues}>
              <FilterTextValues
                onDelete={(key) => {
                  if (key === 'reject_reason') {
                    handleClearReason()
                  } else {
                    const { [key]: _, ...obj } = filter
                    updateFilter(obj)
                  }
                }}
              />
            </div>
          )}

          {isOpenFilter && (
            <AsideFilter
              getFilterParams={handleFilterChange}
              config={filterCfg}
              filterValue={filter}
              onDropFilter={() => {
                updateFilter({})
                setTextValues([])
                closeFilter()
              }}
              translate={translate}
            />
          )}
        </div>
      </div>

      <div className={st.containerClient}>
        {loading ? (
          <Loader fullWidth />
        ) : (
          <table className={st.dealsTable}>
            <thead>
              <tr>
                <th>{translate.deals_name}</th>
                <th>{translate.telephone}</th>
                <th>{translate.whatsapp}</th>
                <th>{translate.responsible}</th>
                <th>{translate.creation_date}</th>
                <th>{translate.deal_stage}</th>
                <th>{translate.reject_reason}</th>
              </tr>
            </thead>
            <tbody>
              {deals?.length > 0 ? (
                deals
                  .slice()
                  .sort(
                    (a, b) => new Date(a.created_at) - new Date(b.created_at),
                  )
                  .map((deal, index) => {
                    const contactInfo = getContactInfo(deal)
                    return (
                      <tr
                        key={`${deal.id}-${index}`}
                        className={index % 2 === 0 ? st.evenRow : st.oddRow}
                      >
                        <td>
                          <Link
                            className={st.clickableDealName}
                            to={`/crm/customer-card/${deal.id}/?funnel_id=${deal.funnels?.[0]?.id}`}
                          >
                            {deal.deal_name}
                          </Link>
                        </td>
                        <td>{contactInfo.phone}</td>
                        <td>{contactInfo.whatsapp || '-'}</td>
                        <td>{deal.manager?.name || ''}</td>
                        <td>
                          {new Date(deal.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div className={st.square}>
                            <div
                              className={st.tips}
                              style={{
                                backgroundColor:
                                  deal.funnels?.[0]?.status_deal?.color
                                    ?.body_color || '#97F4FF',
                              }}
                              data-tooltip={
                                deal.funnels?.[0]?.status_deal?.name
                              }
                            />
                            {deal.status_deal?.name ||
                              deal.funnels?.[0]?.status_deal?.name}
                          </div>
                        </td>
                        <td>{deal.reject_reason}</td>
                      </tr>
                    )
                  })
              ) : (
                <tr>
                  <td colSpan='7' className={st.noData}>
                    Нет данных
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {!loading && meta.count_items > 0 && (
        <div className={st.pagination}>
          <Pagination
            translate={translate}
            limit={+perPage}
            count={meta.count_items || 0}
            page={page}
            onChange={setPage}
            onChangePerPage={setPerPage}
          />
        </div>
      )}
    </div>
  )
}

export default observer(NewDeals)
